import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  DeleteForever as DeleteIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { ContractorLayout } from "../components/layout/ContractorLayout";
import { ClientLayout } from "../components/layout/ClientLayout";

export function Settings() {
  const navigate = useNavigate();
  const { profile, signOut, updatePassword } = useAuth();

  const isContractor = profile?.role === "contractor";
  const backPath = isContractor ? "/contractor" : "/client";

  // ── Password change ──────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setPasswordLoading(true);

    // Re-authenticate with current password first
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: profile?.email ?? "",
      password: currentPassword,
    });

    if (signInError) {
      setPasswordError("Current password is incorrect.");
      setPasswordLoading(false);
      return;
    }

    const { error } = await updatePassword(newPassword);
    setPasswordLoading(false);

    if (error) {
      setPasswordError(error.message);
    } else {
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // ── Delete account ───────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeleteLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY ?? "",
        },
      },
    );

    const json = await res.json();
    setDeleteLoading(false);

    if (!res.ok) {
      setDeleteError(json.error ?? "Failed to delete account.");
      return;
    }

    await signOut();
    navigate("/login", { replace: true });
  };

  const content = (
    <Stack spacing={3}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
      </Box>

      {/* Change password */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <LockIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Change Password
            </Typography>
          </Box>

          <form onSubmit={handlePasswordChange}>
            <Stack spacing={2}>
              {passwordError && <Alert severity="error">{passwordError}</Alert>}
              {passwordSuccess && (
                <Alert severity="success">Password updated successfully!</Alert>
              )}
              <TextField
                label="Current password"
                type="password"
                fullWidth
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <TextField
                label="New password"
                type="password"
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="At least 8 characters"
              />
              <TextField
                label="Confirm new password"
                type="password"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={passwordLoading}
                  startIcon={
                    passwordLoading ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {passwordLoading ? "Updating…" : "Update password"}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card sx={{ border: "1px solid", borderColor: "error.light" }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "error.main" }}>
              Danger Zone
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Permanently delete your account and all associated data. This cannot
            be undone.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteOpen(true)}
          >
            Delete my account
          </Button>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteConfirmText("");
          setDeleteError(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will permanently delete your account and all your data. Type{" "}
              <strong>DELETE</strong> to confirm.
            </Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <TextField
              fullWidth
              placeholder="Type DELETE to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDeleteOpen(false);
              setDeleteConfirmText("");
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== "DELETE" || deleteLoading}
            onClick={handleDeleteAccount}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? "Deleting…" : "Delete account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  return isContractor ? (
    <ContractorLayout>{content}</ContractorLayout>
  ) : (
    <ClientLayout>{content}</ClientLayout>
  );
}

export default Settings;
